import { useState } from "react";
import { useAtomValue } from "jotai";
import { statsDateRangeAtom } from "@/stores/atoms/stats-date-range-atom";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Card, CardFooter } from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import { useFetchActiveUsers } from "../hooks/useFetchActiveUsers";

// Handles both "DD/MM/YYYY" (day) and "DD/MM/YYYY:DD/MM/YYYY" (week/month)
function parseApiDate(value: string): Date {
  const startPart = value.split(":")[0];
  const [day, month, year] = startPart.split("/");
  return new Date(Number(year), Number(month) - 1, Number(day));
}

function formatXTick(value: string, xPeriod: string, locale: string): string {
  const date = parseApiDate(value);
  if (xPeriod === "day") {
    return date.toLocaleDateString(locale, { day: "2-digit", month: "short" });
  }
  return date.toLocaleDateString(locale, { day: "numeric", month: "short" });
}

function formatTooltipLabel(
  value: string,
  xPeriod: XPeriod,
  locale: string,
  tFn: (k: string) => string,
): string {
  if (xPeriod === "month") {
    return parseApiDate(value).toLocaleDateString(locale, { month: "long" });
  }
  if (xPeriod === "week") {
    const [start, end] = value.split(":");
    return end ? `${start} ${tFn("modules.stats.to")} ${end}` : start;
  }
  return parseApiDate(value).toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
  });
}

function formatYTick(value: number): string {
  if (value >= 1000) return `${value / 1000}K`;
  return String(value);
}

const PERIOD_RANK = { day: 0, week: 1, month: 2 } as const;
type XPeriod = keyof typeof PERIOD_RANK;

function maxValidXPeriod(start_date: string, end_date: string): XPeriod {
  const diffDays =
    (new Date(end_date).getTime() - new Date(start_date).getTime()) /
    86_400_000;
  if (diffDays >= 60) return "month";
  if (diffDays >= 14) return "week";
  return "day";
}

const UsersChart = () => {
  const { t, i18n } = useTranslation();
  const [xPeriod, setXPeriod] = useState<XPeriod>("month");
  const [selectedActive, setSelectedActive] = useState<boolean | null>(null);
  const range = useAtomValue(statsDateRangeAtom);

  const maxPeriod = maxValidXPeriod(range.start_date, range.end_date);
  const effectiveXPeriod =
    PERIOD_RANK[xPeriod] <= PERIOD_RANK[maxPeriod] ? xPeriod : maxPeriod;

  const { data } = useFetchActiveUsers({
    x_period: effectiveXPeriod,
    start_date: range.start_date,
    end_date: range.end_date,
    active: selectedActive ?? undefined,
  });

  const chartTitle =
    selectedActive === true
      ? t("modules.stats.users_chart.title_active")
      : t("modules.stats.users_chart.title_all");

  const chartConfig = {
    users: {
      label: t("modules.stats.users_chart.title"),
      color: "var(--chart-2)",
    },
  } satisfies ChartConfig;
  const chartData = (data ?? []).map((point) => ({
    label: point.x,
    users: point.y,
  }));

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between gap-4">
        <p className="font-semibold">{chartTitle}</p>
        <Select
          value={selectedActive === true ? "active" : "all"}
          onValueChange={(v) => setSelectedActive(v === "active" ? true : null)}
        >
          <SelectTrigger className="w-32.5">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="all">
                {t("modules.stats.users_chart.filter_all")}
              </SelectItem>
              <SelectItem value="active">
                {t("modules.stats.users_chart.filter_active")}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <div>
        <ChartContainer config={chartConfig}>
          <AreaChart accessibilityLayer data={chartData}>
            <CartesianGrid vertical={false} />
            <YAxis
              domain={[0, "auto"]}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={formatYTick}
            />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value: string) =>
                formatXTick(value, xPeriod, i18n.language)
              }
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  labelFormatter={(value) =>
                    formatTooltipLabel(
                      String(value),
                      effectiveXPeriod,
                      i18n.language,
                      t,
                    )
                  }
                />
              }
            />
            <defs>
              <linearGradient id="fillUsers" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="var(--color-users)"
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor="var(--color-users)"
                  stopOpacity={0.1}
                />
              </linearGradient>
            </defs>
            <Area
              dataKey="users"
              type="monotone"
              fill="url(#fillUsers)"
              fillOpacity={0.4}
              stroke="var(--color-users)"
              stackId="a"
            />
          </AreaChart>
        </ChartContainer>
      </div>
      <CardFooter>
        <div className="flex w-full justify-center">
          <Select
            value={xPeriod}
            onValueChange={(v) => setXPeriod(v as XPeriod)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t("modules.stats.frequency")} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="day">
                  {t("modules.stats.per_day")}
                </SelectItem>
                <SelectItem value="week" disabled={PERIOD_RANK[maxPeriod] < 1}>
                  {t("modules.stats.per_week")}
                </SelectItem>
                <SelectItem value="month" disabled={PERIOD_RANK[maxPeriod] < 2}>
                  {t("modules.stats.per_month")}
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </CardFooter>
    </Card>
  );
};

export default UsersChart;
